<template>
  <div class="app-container top-padding" uk-height-viewport="expand: true">
    <div class="uk-width-1-1" uk-grid>
      <div class="uk-width-1-4">
        <resourceMenu
          :approvedResources="getApprovedResourcesTotal"
          :declinedResources="getDeclinedResourcesTotal"
          :newApprovedResources="newApprovedResources"
          :newDeclinedResources="newDeclinedResources"
          :newPendingResources="newPendingResources"
          :pendingResources="getPendingResourcesTotal"
          :selectedItem="selectedResourceState"
          @onClick="setSelectedModeratedResource">
        </resourceMenu>
      </div>
      <div class="uk-width-3-4">
        <button
          class="delete-all-declined-button"
          uk-toggle="target: #delete-modal"
          v-if="getDeclinedSelected && (getDeclinedResources.length > 0)">
         {{$t('manageMyResources.deleteAllDeclined')}}
        </button>
        <pagination
          :currentPage="currentPage"
          :nextEnabled="nextEnabled"
          @fetchNextBatch="next"
          @fetchPreviousBatch="previous"
          class="uk-align-right">
        </pagination>
        <div>
          <shared-resources
            :lastPulledDate="lastPulledDate"
            :loading="getApprovedResourcesLoading"
            :resources="getApprovedResources"
            v-if="getApprovedSelected">
          </shared-resources>
          <pending-resources
            :lastPulledDate="lastPulledDate"
            :loading="getPendingResourcesLoading"
            :resources="getPendingResources"
            v-if="getPendingSelected">
          </pending-resources>
          <declined-resources
            :lastPulledDate="lastPulledDate"
            :loading="getDeclinedResourcesLoading"
            :resources="getDeclinedResources"
            v-if="getDeclinedSelected">
          </declined-resources>
        </div>
      </div>
    </div>
    <div class="uk-width-1-1">
      <success-toast :duration="3000" :message="shareMessage" :success="successAlert"></success-toast>
    </div>
    <delete-modal :heading="modalHeading" id="delete-modal" v-on:delete-resource="deleteResources()"></delete-modal>
  </div>
</template>

<script>
  import resourceMenu from './navigation/menu'
  import pagination from '../../search/pagination'
  import sharedResources from './resources/shared-resources'
  import pendingResources from './resources/pending-resources'
  import declinedResources from './resources/declined-resources'
  import {mapActions, mapGetters, mapMutations} from 'vuex'
  import * as _ from 'lodash'
  import * as idStore from '../../../enums/id-store-keys'
  import successToast from '../../ui-components/alerts/success-toast'
  import * as moderationStatus from '../../../enums/moderation-status'
  import * as misc from '../../../enums/misc'
  import deleteModal from '../../ui-components/modals/delete-modal'
  import dateUtil from '../../../utils/date-util'

  export default {
    name: 'manage-resources',
    components: {
      pendingResources,
      sharedResources,
      declinedResources,
      pagination,
      resourceMenu,
      successToast,
      deleteModal
    },
    props: ['loadState'],
    data() {
      return {
        shareMessage: this.$t('manageMyResources.shareMessage'),
        modalHeading: this.$t('manageMyResources.modalHeading'),
        successAlert: null,
        lastPulledDate: null
      }
    },
    metaInfo() {
      return {
        meta: [
          { name: 'robots',
            content: 'noindex'
          }
        ]
      }
    },
    methods: {
      ...mapActions(['fetchIDData', 'storeIDData', 'fetchModeratedResources', 'fetchContentProfile', 'deleteDeclinedResources', 'cleanSetSearchCriteria']),
      ...mapMutations(['setDeclinedModerationResourceDefaults', 'setModeratedResourcePage', 'setModeratedResourceTotal', 'setSelectedModeratedResource']),
      fetchLastPulledDate() {
        this.fetchIDData()
          .then(result => {
            let store = result.body
            if (!_.isEmpty(store)) {
              for (let key in store) {
                if (key === idStore.LAST_PULLED) {
                  this.lastPulledDate = store[key]
                }
              }
            }
          })
      },
      deleteResources() {
        this.deleteDeclinedResources()
          .then(() => {
            this.fetchModeratedResources({status: moderationStatus.DECLINED.toUpperCase(), nextPageToken: null})
            this.setDeclinedModerationResourceDefaults()
          })
      },
      storeLastPulledDate() {
        let keyValue = {[idStore.LAST_PULLED]: Date.now()}
        this.storeIDData(keyValue)
      },
      next() {
        switch (this.selectedResourceState) {
          case moderationStatus.PENDING:
            this.setModeratedResourcePage({status: moderationStatus.PENDING, page: this.getPendingResourcesPage + 1})
            break
          case moderationStatus.SHARED:
            this.setModeratedResourcePage({status: moderationStatus.APPROVED, page: this.getApprovedResourcesPage + 1})
            break
          case moderationStatus.DECLINED:
            this.setModeratedResourcePage({status: moderationStatus.DECLINED, page: this.getDeclinedResourcesPage + 1})
        }
      },
      previous() {
        switch (this.selectedResourceState) {
          case moderationStatus.PENDING:
            this.setModeratedResourcePage({status: moderationStatus.PENDING, page: this.getPendingResourcesPage - 1})
            break
          case moderationStatus.SHARED:
            this.setModeratedResourcePage({status: moderationStatus.APPROVED, page: this.getApprovedResourcesPage - 1})
            break
          case moderationStatus.DECLINED:
            this.setModeratedResourcePage({status: moderationStatus.DECLINED, page: this.getDeclinedResourcesPage - 1})
        }
      },
      resolveModeratedResources() {
        if (_.isEmpty(this.getPendingResources)) {
          this.fetchModeratedResources({status: moderationStatus.PENDING.toUpperCase(), nextPageToken: null})
        }
        if (_.isEmpty(this.getApprovedResources)) {
          this.fetchModeratedResources({status: moderationStatus.APPROVED.toUpperCase(), nextPageToken: null})
        }
        if (_.isEmpty(this.getDeclinedResources)) {
          this.fetchModeratedResources({status: moderationStatus.DECLINED.toUpperCase(), nextPageToken: null})
        }
      }
    },
    computed: {
      ...mapGetters(['getPendingResources', 'getApprovedResources', 'getDeclinedResources', 'getPendingResourcesPage', 'getApprovedResourcesPage', 'getDeclinedResourcesPage', 'getPendingResourcesLoading', 'getApprovedResourcesLoading', 'getDeclinedResourcesLoading', 'getPendingResourcesTotal', 'getApprovedResourcesTotal', 'getDeclinedResourcesTotal', 'getPendingSelected', 'getApprovedSelected', 'getDeclinedSelected']),
      ...mapGetters('auth/id', ['authenticated']),
      currentPage() {
        switch (this.selectedResourceState) {
          case moderationStatus.PENDING:
            return this.getPendingResourcesPage
          case moderationStatus.SHARED:
            return this.getApprovedResourcesPage
          case moderationStatus.DECLINED:
            return this.getDeclinedResourcesPage
        }
      },
      nextEnabled() {
        switch (this.selectedResourceState) {
          case moderationStatus.PENDING:
            let pendingLength = this.getPendingResources.length
            return pendingLength > this.getPendingResourcesPage * misc.PAGING_SIZE
          case moderationStatus.SHARED:
            let approvedLength = this.getApprovedResources.length
            return approvedLength > this.getApprovedResourcesPage * misc.PAGING_SIZE
          case moderationStatus.DECLINED:
            let declinedLength = this.getDeclinedResources.length
            return declinedLength > this.getDeclinedResources * misc.PAGING_SIZE
        }
      },
      selectedResourceState() {
        if (this.getApprovedSelected) {
          return moderationStatus.SHARED
        } else if (this.getPendingSelected) {
          return moderationStatus.PENDING
        } else if (this.getDeclinedSelected) {
          return moderationStatus.DECLINED
        }
      },
      newPendingResources() {
        for (let item of this.getPendingResources) {
          return dateUtil.compareTime(this.lastPulledDate, item.lastModTime)
        }
      },
      newApprovedResources() {
        for (let item of this.getApprovedResources) {
          return dateUtil.compareTime(this.lastPulledDate, item.lastModTime)
        }
      },
      newDeclinedResources() {
        for (let item of this.getDeclinedResources) {
          return dateUtil.compareTime(this.lastPulledDate, item.lastModTime)
        }
      },
      authed() {
        return this.authenticated
      }
    },
    created() {
      if (!this.authed) {
        let state = {
          from: this.$route.path,
          download: false
        }
        this.$auth.id.signIn(state)
      } else {
        this.cleanSetSearchCriteria(null)
        if (this.loadState) {
          this.setSelectedModeratedResource(this.loadState)
        }
        if (this.$route.params.metadata) {
          this.successAlert = this.$route.params.metadata.success
        }
        this.fetchLastPulledDate()
        this.resolveModeratedResources()
        this.storeLastPulledDate()
      }
    }
  }
</script>

<style lang="scss" scoped>
  @import '../../../../src/styles/breakpoints';
  @import '../../../../src/styles/colours';

  .delete-all-declined-button {
    min-width: 342px;
    height: 40px;
    border-radius: 2px;
    box-shadow: 0 1px 3px 0 $drop-shadow-black;
    border: solid 2.8px $input-warning;
    background-color: $input-warning;
    color: white;
    font-size: 18px;
    font-weight: bold;

    &:hover {
      box-shadow: 3px 5px 8px 0 $drop-shadow-black;
      border: solid 2.8px white;
      background-color: white;
      color: $input-warning;
    }
  }

  .top-padding {
    padding-top: 86px;
  }

  .uk-align-right {
    margin-bottom: 18px;
  }

  @media screen and (max-width: $breakpoint-medium) {
    .app-container {
      width: 100%;
    }

    .top-padding {
      padding-top: 115px;
    }
  }
</style>
